import {z} from "zod/v4-mini"
import {graphNode} from "@cm/graph-v3"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {GeometryValueNode} from "#template/nodes/geometry-graph/value/value-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {AttributeRef, ValueRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import {
    applyGeometryOperator,
    resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type PackOperatorParameters<TAttribute> = {
    name: string
    x: TAttribute | null
    y: TAttribute | GeometryValueNode | NumberLike | null
    z?: TAttribute | GeometryValueNode | NumberLike | null
}

export const packOperatorParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        name: z.string(),
        x: z.nullable(attributeSchema),
        y: z.nullable(z.union([attributeSchema, graphNode(GeometryValueNode), numberLike])),
        z: z.optional(z.nullable(z.union([attributeSchema, graphNode(GeometryValueNode), numberLike]))),
    })

function applyPackOperator<T extends IndexingDim>(
    x: GeometryAttributeData<T>,
    y: GeometryAttributeData<T> | GeometryValueData | number,
    z: GeometryAttributeData<T> | GeometryValueData | number | undefined,
): GeometryAttributeData<T> {
    const xAttrRefData = x.ref
    const yAttrRefData = resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber(y)
    const zAttrRefData = z === undefined ? undefined : resolveGeometryAttributeDataOrGeometryAttrributeValueDataOrNumber(z)

    const packAttrRefData = applyGeometryOperator(
        (xAttRef: AttributeRef, yAttrRef: AttributeRef | ValueRef | number, zAttrRef: AttributeRef | ValueRef | number | undefined) =>
            Operators.pack(xAttRef, yAttrRef, zAttrRef),
        xAttrRefData,
        yAttrRefData,
        zAttrRefData,
    )

    return createGeometryAttributeData(packAttrRefData, x.dim)
}

export abstract class GeometryPackOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, PackOperatorParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: PackOperatorParameters<TAttribute>,
    ): parameters is PackOperatorParameters<TAttribute> & NonNullableParameters<PackOperatorParameters<TAttribute>> {
        return parameters.x !== null && parameters.y !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<PackOperatorParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {x, y, z} = parameters

        const [evaluatedXValid, evaluatedXInvalid] = scope.branch(evaluator.evaluate(x))
        const [evaluatedYValid, evaluatedYInvalid] = scope.branch(evaluator.evaluate(y))
        const evaluatedZ = scope.pureLambda(z === null || z === undefined ? null : evaluator.evaluate(z), (z) => (z === null ? undefined : z), "evaluateZ")

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedXValid, evaluatedYValid, evaluatedZ),
            ([x, y, z]) => {
                if (z === null) return null
                return applyPackOperator(x, y, z)
            },
            "applyPackOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(evaluatedXInvalid, evaluatedYInvalid)}
    }
}
